import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle
} from "@fluentui/react-components";
import { FormattedMessage, useIntl } from "react-intl";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { fetchReticulumAuthenticated } from "../../utils/phoenix-utils";

const AVATARS_API = "/api/v1/avatars";

export function DeleteAvatarDialog({ avatarId, open, onOpenChange, onSave }) {
  const intl = useIntl();

  const [avatar, setAvatar] = useState(null);

  const fetchAvatar = useCallback(async avatarId => {
    const { avatars } = await fetchReticulumAuthenticated(`${AVATARS_API}/${avatarId}`);
    return avatars[0];
  }, []);

  useEffect(() => {
    if (avatarId) {
      fetchAvatar(avatarId).then(avatar => {
        setAvatar(avatar);
      });
    }
  }, [avatarId, fetchAvatar]);

  const deleteAvatar = useCallback(async () => {
    await fetchReticulumAuthenticated(`${AVATARS_API}/${avatarId}`, "DELETE");
    onOpenChange(null, { open: false });
    onSave();
  }, [avatarId, onOpenChange, onSave]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label={intl.formatMessage({ id: "room-settings-sidebar.close-button", defaultMessage: "Close" })}
                icon={<Dismiss24Regular />}
                onClick={() => onOpenChange(null, { open: false })}
              />
            }
          >
            <FormattedMessage id="avatar-editor.delete-avatar" defaultMessage="Delete avatar" />
          </DialogTitle>
          <DialogContent>
            <FormattedMessage
              id="avatar-editor.delete-avatar.confirmation-prompt"
              defaultMessage="Are you sure you want to delete the avatar: {avatarName}?"
              values={{ avatarName: avatar?.name }}
            />
          </DialogContent>
          <DialogActions>
            <Button appearance="outline" onClick={() => onOpenChange(null, { open: false })}>
              <FormattedMessage id="avatar-editor.delete-avatar.cancel" defaultMessage="No" />
            </Button>
            <Button appearance="primary" onClick={deleteAvatar}>
              <FormattedMessage id="avatar-editor.delete-avatar.confirm" defaultMessage="Yes" />
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

DeleteAvatarDialog.propTypes = {
  avatarId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onSave: PropTypes.func
};
